import { Socket, io } from "socket.io-client";
import utilService from '@/services/util.service';
import { trackError, trackEvent } from '@/helpers/tracking.helpers';
import { SocketEvents, TrackingEvents } from '@/helpers/enum';
import { SocketStateManager } from './socket-state.singleton';
import {
    DEFAULT_ACK_TIMEOUT, DEFAULT_RECONNECT_ATTEMPTS, DEFAULT_RECONNECT_DELAY,
    DEFAULT_PING_INTERVAL, SOCKET_LOGGING_ENABLED, PING_STOCK_ID
} from "../constants";

type EventHandler<T = any> = (...args: T[]) => void;
type AckCallback<T = any> = (response?: T, error?: Error) => void;
type EmitOperation<T = any> = {
    event: string;
    data?: any;
    callback?: AckCallback<T>;
    resolve?: (value: T | PromiseLike<T>) => void;
    reject?: (reason?: any) => void;
};

class SocketConnectionBuilder {
    private static instance: SocketConnectionBuilder | null = null;
    private socket: Socket | null = null;
    private state = SocketStateManager.getInstance();
    private eventHandlers: Map<string, EventHandler[]> = new Map();
    private enableLogging = SOCKET_LOGGING_ENABLED;
    private SOCKET_TAG = "SocketConnectionBuilder :";
    private connectionOptions: any = {
        transports: [SocketEvents.Transport],
        reconnectionAttempts: DEFAULT_RECONNECT_ATTEMPTS,
        reconnectionDelay: DEFAULT_RECONNECT_DELAY
    };
    private isConnected = false;
    private autoReconnect = true; // New: Auto-reconnect flag
    private isConnecting = false; // New: Connection state tracker
    private pingInterval: NodeJS.Timeout | null = null;

    private static instanceCount = 0;
    private instanceId: number;

    private constructor() {
        this.instanceId = ++SocketConnectionBuilder.instanceCount;
        console.log(`${this.SOCKET_TAG} Instance ${this.instanceId} created`);
    }

    static create(): SocketConnectionBuilder {
        if (!SocketConnectionBuilder.instance) {
            SocketConnectionBuilder.instance = new SocketConnectionBuilder();
        }
        // SocketConnectionBuilder.instance.setupHealthChecks();
        return SocketConnectionBuilder.instance;
    }

    enableAutoReconnect(enable: boolean): this {
        this.autoReconnect = enable;
        return this;
    }

    private setupHealthChecks() {
        if (!this.socket) return;

        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }

        this.pingInterval = setInterval(() => {
            if (this.socket?.connected) {
                const start = Date.now();
                if (this.enableLogging) console.log(`${this.SOCKET_TAG} Ping Initiated..`, this.state.stockIds.length);

                this.emitWithCallback(SocketEvents.ping, {}, (response: any, error?: any) => {
                    if (error) {
                        if (this.enableLogging) console.error(`${this.SOCKET_TAG} Ping failed`, error);
                        return;
                    }

                    const latency = Date.now() - start;
                    if (this.enableLogging) console.log(`${this.SOCKET_TAG} Ping response received (${latency}ms)`, response);
                });
            }
        }, DEFAULT_PING_INTERVAL);

        this.state.addCleanup(() => {
            clearInterval(this.pingInterval)
            this.pingInterval = null;
        });
    }

    withUrl(url: string): this {
        this.connectionOptions.url = url;
        return this;
    }

    withOption(key: string, value: any): this {
        this.connectionOptions[key] = value;
        return this;
    }

    on<T = any>(event: string, handler: EventHandler<T>): this {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event)?.push(handler);

        if (this.socket) {
            this.socket.on(event, handler);
        }

        return this;
    }

    emit(event: string, data?: any): this {
        if (!this.isConnected || !this.socket?.connected) {
            console.error(`${this.SOCKET_TAG} Cannot emit - socket not connected`);
            return this;
        }
        this.socket.emit(event, data);
        return this;
    }

    emitWithCallback<T = any>(
        event: string,
        data: any,
        callback: AckCallback<T>,
        isStringify: boolean = true
    ): this {
        if (!this.isConnected || !this.socket?.connected) {
            console.error(`${this.SOCKET_TAG} Cannot emit - socket not connected`);
            callback(undefined, new Error('Socket not connected'));
            return this;
        }

        const operationData = isStringify ? JSON.stringify(data) : data;
        let timeoutId: NodeJS.Timeout | null = setTimeout(() => {
            timeoutId = null; // Mark as cleared
            if (this.enableLogging) {
                const error = new Error(`Server did not respond within ${DEFAULT_ACK_TIMEOUT}ms`);
                console.error(`${this.SOCKET_TAG} ${error.message} for event ${event}`);
                callback(undefined, error);
            }

        }, DEFAULT_ACK_TIMEOUT);

        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        this.ensureConnected()
            .then(() => {
                if (this.enableLogging) console.log(`${this.SOCKET_TAG} is Connected Initiating Emit.`);

                try {
                    this.socket.emit(event, operationData, (response: any) => {
                        cleanup(); // Clear timeout when response is received

                        if (!response) {
                            const error = new Error('Empty server response');
                            console.error(`${this.SOCKET_TAG} ${error.message} for event ${event}`);
                            callback(undefined, error);
                            return;
                        }

                        if (response.error || response.status === 'error') {
                            const error = new Error(response.error || response.message);
                            console.error(`${this.SOCKET_TAG} Error response for ${event}:`, error.message);
                            callback(undefined, error);
                            return;
                        }

                        try {
                            const parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;
                            if (this.enableLogging) {
                                console.log(`${this.SOCKET_TAG} Success response for ${event}`);
                            }
                            callback(parsedResponse, undefined);
                        } catch (parseError) {
                            console.error(`${this.SOCKET_TAG} Error parsing response for ${event}:`, parseError);
                            callback(undefined, parseError instanceof Error ? parseError : new Error(String(parseError)));
                        }
                    });
                } catch (emitError) {
                    cleanup();
                    console.error(`${this.SOCKET_TAG} Emit failed for ${event}:`, emitError);
                    callback(undefined, emitError instanceof Error ? emitError : new Error(String(emitError)));
                }
            })
            .catch(error => {
                cleanup();
                console.error(`${this.SOCKET_TAG} Emit failed for ${event}:`, error);
                callback(undefined, error);
            });

        return this;
    }

    emitWithPromise<T = any>(event: string, data?: any, isStringify: boolean = true): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const operationData = isStringify ? JSON.stringify(data) : data;
            const timeoutId = setTimeout(() => {
                const error = new Error(`Server did not respond within ${DEFAULT_ACK_TIMEOUT}ms`);
                if (this.enableLogging) console.error(`${this.SOCKET_TAG} ${error.message} for event ${event}`);
                reject(error);
            }, DEFAULT_ACK_TIMEOUT);

            this.ensureConnected()
                .then(() => {
                    if (this.enableLogging) console.log(`${this.SOCKET_TAG} is Connected Initiating Emit.`);
                    try {
                        this.socket.emit(event, operationData, (response: any) => {
                            clearTimeout(timeoutId);

                            if (!response) {
                                const error = new Error('Empty server response');
                                console.error(`${this.SOCKET_TAG} ${error.message} for event ${event}`);
                                reject(error);
                                return;
                            }

                            if (response.error || response.status === 'error') {
                                const error = new Error(response.error || response.message);
                                console.error(`${this.SOCKET_TAG} Error response for ${event}:`, error.message);
                                reject(error);
                                return;
                            }

                            if (this.enableLogging) {
                                console.log(`${this.SOCKET_TAG} Success response for ${event}`);
                            }
                            resolve(response.data || response);
                        });
                    } catch (emitError) {
                        clearTimeout(timeoutId);
                        console.error(`${this.SOCKET_TAG} Emit failed for ${event}:`, emitError);
                        reject(emitError);
                    }
                })
                .catch(error => {
                    console.error(`${this.SOCKET_TAG} Emit failed for ${event}:`, error);
                    reject(error);
                });
        });
    }

    private ensureConnected(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isConnected && this.socket?.connected) {
                resolve();
                return;
            }

            const onConnect = () => {
                cleanup();
                resolve();
            };

            const onConnectError = (error: any) => {
                cleanup();
                reject(error);
            };

            const cleanup = () => {
                if (this.enableLogging) console.log(`${this.SOCKET_TAG} Cleanup Initiated`);
                this.socket?.off('connect', onConnect);
                this.socket?.off('connect_error', onConnectError);
            };

            this.socket?.on('connect', onConnect);
            this.socket?.on('connect_error', onConnectError);
            if (this.enableLogging) console.log(`${this.SOCKET_TAG} Connection Re establishing..`);

            if (!this.socket?.active) {
                if (this.enableLogging) console.log(`${this.SOCKET_TAG} Connection Re Establishing..`);
                try {
                    this.connect();
                } catch (error) {
                    cleanup();
                    reject(error);
                }
            }
        });
    }

    connect(): Socket {
        if (this.socket && this.isConnected) return this.socket;
        if (this.isConnecting && this.socket) return this.socket!;

        try {
            this.isConnecting = true;

            // Only cleanup if we have an existing socket that's not connecting
            if (this.socket && !this.isConnecting) {
                this.cleanupSocket();
            }

            const url = this.connectionOptions.url || utilService.getLiveHostURL();
            // console.log(`${this.SOCKET_TAG} Connecting to socket at ${url}`);

            this.socket = io(url, this.connectionOptions);
            this.state.socket = this.socket;

            this.setupConnectionHandlers();
            this.registerEventHandlers();
            this.setupCleanup();

            this.socket.connect();
            return this.socket;
        } catch (error) {
            this.isConnecting = false;
            trackSocketError(error, TrackingEvents.socketConnectionInitiated);
            throw error;
        }
    }
    private cleanupSocket(): void {
        if (this.socket) {
            // Remove all event listeners
            this.socket.removeAllListeners();

            // Disconnect if connected
            if (this.socket.connected) {
                this.socket.disconnect();
            }

            // Clear references
            this.socket = null;
            this.isConnected = false;
            this.isConnecting = false;

            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Cleaned up previous socket instance`);
            }
        }
    }

    disconnect(): void {
        if (this.socket) {
            if (this.enableLogging) console.log(`${this.SOCKET_TAG} Disconnecting..`);
            this.socket.disconnect();
            this.isConnected = false;
        }
    }

    private setupConnectionHandlers(): void {
        if (!this.socket) return;

        this.socket.on(SocketEvents.connect, () => {
            this.isConnecting = false;
            this.isConnected = true;
            if (this.enableLogging) console.log(`${this.SOCKET_TAG} Socket connected`);
            trackSocketEvent(TrackingEvents.socketConnected, { url: this.connectionOptions.url });
            this.invokeHandlers(SocketEvents.connect);
            this.setupHealthChecks();
        });

        this.socket.on('disconnect', (reason) => {
            this.isConnected = false;
            trackSocketEvent(TrackingEvents.socketDisconnected, { reason });
            this.invokeHandlers(SocketEvents.disconnect, reason);

            if (this.autoReconnect) {
                if (this.enableLogging) console.log(`${this.SOCKET_TAG} Attempting to reconnect...`);
                setTimeout(() => this.connect(), 1000); // Reconnect after 1s
            }
        });

        this.socket.on(SocketEvents.connect_error, (error) => {
            trackSocketError(error, TrackingEvents.socketConnectionError);
            this.invokeHandlers(SocketEvents.connect_error, error);
        });

        this.socket.on(SocketEvents.error, (error) => {
            trackSocketError(SocketEvents.error, TrackingEvents.socketError);
            this.invokeHandlers(SocketEvents.error, error);
        });
    }

    private registerEventHandlers(): void {
        if (!this.socket) return;

        this.eventHandlers.forEach((handlers, event) => {
            handlers.forEach(handler => {
                this.socket?.on(event, handler);
            });
        });
    }

    private setupCleanup(): void {
        this.state.addCleanup(() => {
            if (this.enableLogging) console.log("Socket Builder: Cleanup Initiated");
            if (this.socket) {
                // this.socket.removeAllListeners();
                // this.socket.close();
            }
        });
    }

    private invokeHandlers(event: string, ...args: any[]): void {
        const handlers = this.eventHandlers.get(event) || [];
        handlers.forEach(handler => handler(...args));
    }
}

// Helper functions
const trackSocketError = (error: any, context: string, metadata?: Record<string, any>) => {
    trackError(error, {
        context: `Socket:${context}`,
        ...metadata
    });
};

const trackSocketEvent = (eventName: string, metadata?: Record<string, any>) => {
    trackEvent(eventName, metadata);
};

export default SocketConnectionBuilder;